import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ApiResponseBackupShow } from '../types/api/ApiResponseBackup';
import type { BackupDetail } from '../types/models/Backup';

/** Recent runs embedded in the detail payload. The full list is paginated separately. */
const EMBEDDED_RUNS = 30;
/** Chains to embed — the documented maximum, since a `members` blob can reach 256 KB. */
const EMBEDDED_DISKS = 500;

/**
 * `GET /datacenters/backups/{id}` — one subject with its disk chains (including members), the
 * libvirt spec captured at the last backup, and the most recent runs.
 *
 * A **missing** subject is returned normally here (with `missing_since` set): `include_missing`
 * only governs the list, so a user following a stored link must still be able to open the row and
 * read *why* it disappeared. Do not treat that as a not-found.
 *
 * No-ops while `id` is `null`, so a caller can render the not-found state for a non-numeric route
 * parameter without firing a request.
 */
export function useBackup(id: number | null) {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [backup, setBackup] = useState<BackupDetail | null>(null);
  const [loading, setLoading] = useState(id !== null);
  const [error, setError] = useState<Error | null>(null);

  const fetchBackup = useCallback(async () => {
    if (id === null || !auth.user?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch(`/datacenters/backups/${id}?runs=${EMBEDDED_RUNS}&disks=${EMBEDDED_DISKS}`);
      const json = (await response.json().catch(() => null)) as ApiResponseBackupShow | null;

      if (json === null) {
        throw new Error(`Unexpected response from server (HTTP ${response.status})`);
      }
      if (json.status !== 'success' || !json.data) {
        throw new Error(json.message ?? 'Unexpected response from server');
      }

      setBackup(json.data.backup);
    } catch (err) {
      setBackup(null);
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      setLoading(false);
    }
  }, [id, auth.user?.access_token, apiFetch]);

  useEffect(() => {
    fetchBackup();
  }, [fetchBackup]);

  return { backup, loading, error, refetch: fetchBackup };
}
